/**
 * Collection rows
 *
 * One row per collection: a feature card and that collection's products in a sideways-sliding
 * row with arrow buttons. The first row is server-rendered; the others arrive as placeholders and
 * are fetched from their collection page shortly before they scroll into view, so a homepage
 * carrying six rows still ships one row's worth of product markup. A product card is ~16KB of
 * HTML in this theme, so rendering every row up front would add about a megabyte.
 *
 * Until 10/10/2026 this was a tabbed grid (one grid, a row of pills swapping its contents) — the
 * element and file keep the `collection-tabs` name so the section and its saved settings carry on.
 *
 * Every row's title and feature card are real links, so with JavaScript off (or a fetch failing)
 * the collection is still one click away; only the product row stays as placeholders.
 */

/**
 * The collection template's main section id, e.g. `template--123__main`. Learned from the first
 * full collection page fetched, then reused so later rows can ask the Section Rendering API for
 * the grid alone rather than a whole page. Shared across instances — the id is per-theme.
 */
let mainSectionId = null;

class CollectionTabs extends HTMLElement {
  /** @type {Map<string, Promise<void>>} handle -> load in progress or done */
  #loads = new Map();
  /** @type {IntersectionObserver | null} */
  #viewObserver = null;
  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  connectedCallback() {
    this.rows = Array.from(this.querySelectorAll('[data-tabs-row]'));
    if (!this.rows.length) return;

    if ('ResizeObserver' in window) {
      this.#resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) this.#updateArrows(entry.target.closest('[data-tabs-row]'));
      });
    }

    for (const row of this.rows) this.#setUpRail(row);

    this.#loadWhenNear();
    this.#prefetchWhenIdle();
  }

  disconnectedCallback() {
    this.#viewObserver?.disconnect();
    this.#resizeObserver?.disconnect();
  }

  /**
   * Arrow buttons for a row. Each press moves a little under one screenful, so the last card of
   * one view is the first of the next and nobody loses their place. An arrow is disabled when
   * there is nothing further that way.
   *
   * @param {HTMLElement} row
   */
  #setUpRail(row) {
    const grid = row.querySelector('[data-tabs-grid]');
    const prev = row.querySelector('[data-tabs-prev]');
    const next = row.querySelector('[data-tabs-next]');
    if (!grid) return;

    const step = (direction) => () =>
      grid.scrollBy({ left: direction * grid.clientWidth * 0.85, behavior: 'smooth' });

    prev?.addEventListener('click', step(-1));
    next?.addEventListener('click', step(1));
    grid.addEventListener('scroll', () => this.#updateArrows(row), { passive: true });
    this.#resizeObserver?.observe(grid);

    this.#updateArrows(row);
  }

  /** @param {HTMLElement | null} row */
  #updateArrows(row) {
    if (!row) return;
    const grid = row.querySelector('[data-tabs-grid]');
    const prev = row.querySelector('[data-tabs-prev]');
    const next = row.querySelector('[data-tabs-next]');
    if (!grid || !prev || !next) return;

    const max = grid.scrollWidth - grid.clientWidth;
    // 1px of slack: sub-pixel widths mean scrollLeft never lands exactly on 0 or max.
    prev.disabled = grid.scrollLeft <= 1;
    next.disabled = !row.hasAttribute('data-loaded') || max <= 1 || grid.scrollLeft >= max - 1;
  }

  /**
   * Fetch a row's products a screen or so before it scrolls into view. The margin is generous
   * because the fetch plus the first images take a moment, and a row that is still placeholders
   * when it arrives on screen is the one thing this is here to avoid.
   */
  #loadWhenNear() {
    const pending = this.rows.filter((row) => !row.hasAttribute('data-loaded'));
    if (!pending.length) return;

    if (!('IntersectionObserver' in window)) {
      for (const row of pending) this.#load(row);
      return;
    }

    this.#viewObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          this.#viewObserver?.unobserve(entry.target);
          this.#load(/** @type {HTMLElement} */ (entry.target));
        }
      },
      { rootMargin: '900px 0px' }
    );

    for (const row of pending) this.#viewObserver.observe(row);
  }

  /**
   * Once the browser is idle, fetch the remaining rows one at a time so they are already there by
   * the time anyone scrolls this far — without competing with images still coming in, and not at
   * all for people who are paying for data.
   */
  #prefetchWhenIdle() {
    const connection = navigator.connection;
    if (connection?.saveData) return;
    if (connection?.effectiveType && /2g|3g/.test(connection.effectiveType)) return;

    const queue = this.rows.filter((row) => !row.hasAttribute('data-loaded'));

    const schedule = (task) =>
      'requestIdleCallback' in window ? requestIdleCallback(task, { timeout: 4000 }) : setTimeout(task, 1200);

    const next = () => {
      const row = queue.shift();
      if (!row) return;
      this.#load(row).then(() => schedule(next));
    };

    schedule(next);
  }

  /**
   * Fills a row with its products. Safe to call more than once: the scroll observer and the idle
   * queue both ask, and they share one request.
   *
   * @param {HTMLElement} row
   * @returns {Promise<void>}
   */
  #load(row) {
    const handle = row.dataset.handle;
    if (!handle || row.hasAttribute('data-loaded')) return Promise.resolve();

    const inFlight = this.#loads.get(handle);
    if (inFlight) return inFlight;

    const grid = row.querySelector('[data-tabs-grid]');
    const request = this.#fetchCards(handle)
      .then((html) => {
        if (!grid) return;
        grid.innerHTML = html;
        grid.scrollLeft = 0;
        row.setAttribute('data-loaded', '');
        this.#updateArrows(row);
      })
      // Nothing to show and no way to recover in place: the row keeps its placeholders, and its
      // title and feature card still link to the collection.
      .catch(() => {});

    this.#loads.set(handle, request);
    return request;
  }

  /**
   * @param {string} handle
   * @returns {Promise<string>}
   */
  async #fetchCards(handle) {
    const perTab = Number(this.dataset.perTab) || 10;
    const url = mainSectionId
      ? `/collections/${handle}?section_id=${mainSectionId}`
      : `/collections/${handle}`;

    const response = await fetch(url);
    if (!response.ok) throw new Error(`Could not load ${handle}: ${response.status}`);

    const text = await response.text();
    if (!mainSectionId) {
      const match = text.match(/shopify-section-(template--\d+__main)\b/);
      if (match) mainSectionId = match[1];
    }

    const doc = new DOMParser().parseFromString(text, 'text/html');
    // The collection grid wraps each card in `.resource-list__item`; fall back to the cards
    // themselves in case that wrapper ever changes, since the card is what carries the styling.
    let items = Array.from(doc.querySelectorAll('.resource-list__item'));
    if (!items.length) items = Array.from(doc.querySelectorAll('product-card'));
    if (!items.length) throw new Error(`No products found for ${handle}`);

    return dropLookalikes(items)
      .slice(0, perTab)
      .map((item) =>
        item.classList.contains('resource-list__item')
          ? item.outerHTML
          : `<div class="resource-list__item">${item.outerHTML}</div>`
      )
      .join('');
  }
}

/**
 * Words of a card's product handle, without the date code in front and the "id00-ds12" tail.
 * @param {Element} item
 * @returns {Set<string>}
 */
function handleWords(item) {
  const href = item.querySelector('a[href*="/products/"]')?.getAttribute('href') || '';
  const handle = (href.split('/products/')[1] || '').split(/[?#]/)[0];
  return new Set(handle.split('-').filter((word) => word && !/^\d+$/.test(word) && !/^(id|ds)\d+$/.test(word)));
}

/**
 * 12/10/2026 — the store lists some designs twice under nearly the same name ("You Are Loved
 * Jewelry Box Organizer…" and "You Are Jewelry Box Organizer…"), and a collection sorted by name
 * puts the twins side by side, so a row showed the same picture twice. Keep the first of each
 * pair: a card is dropped when its handle shares at least 85% of its words with one already kept.
 * Different designs on the same product type share far fewer (measured: 0.75 at most).
 * @param {Element[]} items
 * @returns {Element[]}
 */
function dropLookalikes(items) {
  /** @type {Set<string>[]} */
  const kept = [];
  return items.filter((item) => {
    const words = handleWords(item);
    if (!words.size) return true;
    const twin = kept.some((other) => {
      let shared = 0;
      for (const word of words) if (other.has(word)) shared++;
      return shared / (words.size + other.size - shared) >= 0.85;
    });
    if (!twin) kept.push(words);
    return !twin;
  });
}

if (!customElements.get('collection-tabs')) {
  customElements.define('collection-tabs', CollectionTabs);
}
